/**
 * Admin app-settings hooks. Today: signup_invite_only.
 *
 * Separate from useAdminUsers because the underlying API surface is at
 * /admin/settings (one endpoint, returns all flags) — not nested under
 * users. As more flags appear (maintenance mode, feature toggles)
 * they'll all live in this hook.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';

export interface AdminAppSettings {
  signupInviteOnly: boolean;
}

const adminAppSettingsKey = ['f', 'admin', 'app-settings'] as const;

export function useAdminAppSettings() {
  return useQuery<AdminAppSettings>({
    queryKey: adminAppSettingsKey,
    queryFn: async () => {
      const data = await api.get<{ settings: AdminAppSettings }>('/admin/settings');
      return data.settings;
    },
    staleTime: 30_000,
  });
}

export function useUpdateAdminAppSettings() {
  const qc = useQueryClient();
  return useMutation<AdminAppSettings, Error, Partial<AdminAppSettings>>({
    mutationFn: async (patch) => {
      const data = await api.patch<{ settings: AdminAppSettings }>('/admin/settings', patch);
      return data.settings;
    },
    onSuccess: (settings) => {
      qc.setQueryData(adminAppSettingsKey, settings);
      toast.success('Settings saved');
    },
    onError: () => {
      toast.error('Could not save settings');
    },
  });
}
